const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Power Series Anatomy ──────────────────────────────
function DiagramPowerSeries() {
  const W = 900, H = 360;
  // Left panel: partial sums converging to e^x
  const LP = 14, LW = 400;
  const ox = LP + 60, oy = 240, sc = 44;
  const tx = x => ox + x * sc;
  const ty = y => oy - y * sc;

  // right panel
  const RP = LP + LW + 10, RW = W - RP - 14;

  // Compute partial sum of e^x up to degree n at x
  function expN(x, n) {
    let s = 0, term = 1;
    for (let k = 0; k <= n; k++) {
      s += term;
      term *= x / (k + 1);
    }
    return s;
  }

  // Build polyline points for partial sums
  const xVals = [];
  for (let i = -30; i <= 50; i++) xVals.push(i * 0.1);

  function polyPts(n) {
    return xVals
      .map(x => {
        const y = expN(x, n);
        if (y > 4.5 || y < -1.5) return null;
        return `${tx(x)},${ty(y)}`;
      })
      .filter(Boolean)
      .join(' ');
  }

  // True e^x polyline
  const exPts = xVals
    .map(x => {
      const y = Math.exp(x);
      if (y > 4.5 || y < -1.5) return null;
      return `${tx(x)},${ty(y)}`;
    })
    .filter(Boolean)
    .join(' ');

  const curves = [
    { n: 1, color: '#e07b00', label: 'n=1: 1+x' },
    { n: 2, color: '#c0392b', label: 'n=2: 1+x+x²/2' },
    { n: 4, color: '#9b59b6', label: 'n=4' },
    { n: 6, color: '#27ae60', label: 'n=6' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Power Series — Partial Sums Converging to eˣ, and Series Anatomy</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left panel */}
        <rect x={LP} y={10} width={LW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={LP + LW / 2} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Partial Sums Approximating eˣ</text>

        {/* Grid */}
        {[-1, 0, 1, 2, 3, 4].map(v => (
          <g key={v}>
            <line x1={tx(-1)} y1={ty(v)} x2={tx(4.5)} y2={ty(v)} stroke="#eee" strokeWidth="1" />
            <text x={LP + 22} y={ty(v) + 4} fontSize="11" fill="#bbb" textAnchor="end">{v}</text>
          </g>
        ))}
        {[-1, 0, 1, 2, 3, 4].map(v => (
          <line key={v} x1={tx(v)} y1={ty(-1)} x2={tx(v)} y2={ty(4.5)} stroke="#eee" strokeWidth="1" />
        ))}

        {/* Axes */}
        <line x1={tx(-1)} y1={oy} x2={tx(4.6)} y2={oy} stroke="#1F3864" strokeWidth="1.6" />
        <line x1={ox} y1={ty(4.5)} x2={ox} y2={ty(-1)} stroke="#1F3864" strokeWidth="1.6" />
        <polygon points={`${tx(4.6)},${oy} ${tx(4.6) - 8},${oy - 4} ${tx(4.6) - 8},${oy + 4}`} fill="#1F3864" />
        <polygon points={`${ox},${ty(4.5)} ${ox - 4},${ty(4.5) + 8} ${ox + 4},${ty(4.5) + 8}`} fill="#1F3864" />
        <text x={tx(4.6) + 6} y={oy + 4} fontSize="14" fill="#1F3864" fontWeight="700">x</text>
        <text x={ox + 5} y={ty(4.5) - 4} fontSize="14" fill="#1F3864" fontWeight="700">y</text>
        {[-1, 1, 2, 3, 4].map(v => (
          <text key={v} x={tx(v)} y={oy + 16} textAnchor="middle" fontSize="11" fill="#bbb">{v}</text>
        ))}

        {/* Partial sum curves */}
        {curves.map(({ n, color }) => (
          <polyline key={n} points={polyPts(n)} fill="none" stroke={color} strokeWidth="1.8" strokeDasharray="5,3" />
        ))}

        {/* True e^x */}
        <polyline points={exPts} fill="none" stroke="#2E75B6" strokeWidth="2.5" />

        {/* Legend */}
        {[
          { color: '#2E75B6', label: 'eˣ (true)', dash: false },
          ...curves.map(c => ({ color: c.color, label: c.label, dash: true })),
        ].map(({ color, label, dash }, i) => (
          <g key={i}>
            <line x1={LP + 18} y1={290 + i * 16} x2={LP + 46} y2={290 + i * 16}
              stroke={color} strokeWidth={dash ? 1.6 : 2.5} strokeDasharray={dash ? '5,3' : ''} />
            <text x={LP + 52} y={294 + i * 16} fontSize="11" fill={color} fontWeight="700">{label}</text>
          </g>
        ))}

        {/* Right panel: anatomy */}
        <rect x={RP} y={10} width={RW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={RP + RW / 2} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Series Anatomy</text>

        {/* Big formula display */}
        <rect x={RP + 8} y={38} width={RW - 16} height={46} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5" />
        <text x={RP + RW / 2} y={57} textAnchor="middle" fontSize="13" fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">y = Σ aₙ(x − x₀)ⁿ</text>
        <text x={RP + RW / 2} y={75} textAnchor="middle" fontSize="11" fill="#555" fontFamily="'Courier New',monospace">n = 0, 1, 2, 3, ...</text>

        {/* Annotated parts */}
        {[
          { y: 100, bg: '#f0fff4', bc: '#27ae60', label: 'aₙ', desc: 'Coefficients', note: 'Determined by substitution into ODE' },
          { y: 152, bg: '#fff7ee', bc: '#e07b00', label: 'x₀', desc: 'Center (expansion point)', note: 'Ordinary pt: series converges near x₀' },
          { y: 204, bg: '#f5d0ff', bc: '#9b59b6', label: 'n', desc: 'Power (index)', note: 'Each term: aₙ(x−x₀)ⁿ' },
          { y: 256, bg: '#EBF3FB', bc: '#2E75B6', label: 'R', desc: 'Radius of convergence', note: '|x − x₀| < R  for convergence' },
        ].map(({ y, bg, bc, label, desc, note }) => (
          <g key={label}>
            <rect x={RP + 8} y={y} width={RW - 16} height={46} rx={3} fill={bg} stroke={bc} strokeWidth="1" />
            <text x={RP + 22} y={y + 16} fontSize="13" fill={bc} fontFamily="'Courier New',monospace" fontWeight="700">{label}</text>
            <text x={RP + 44} y={y + 16} fontSize="12" fill="#1F3864" fontWeight="700">— {desc}</text>
            <text x={RP + 18} y={y + 34} fontSize="11" fill="#444">{note}</text>
          </g>
        ))}

      </svg>
      <div style={S.dCaption}>A power series solution expresses y as an infinite polynomial in (x − x₀). Each partial sum (dashed) approximates the true solution; higher-degree partial sums converge over a larger interval determined by the radius of convergence R.</div>
    </div>
  );
}

// ── Diagram 2: Ordinary vs Singular Points ──────────────────────
function DiagramSingularPoints() {
  const W = 900, H = 350;
  const PW = (W - 42) / 3;

  // Panel centers
  const p = i => 14 + i * (PW + 7);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Classification of Points — Ordinary, Regular Singular, and Irregular Singular</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Panel 1: Ordinary point */}
        <rect x={p(0)} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={p(0) + PW / 2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Ordinary Point</text>

        {/* Number line */}
        <line x1={p(0) + 20} y1={120} x2={p(0) + PW - 20} y2={120} stroke="#1F3864" strokeWidth="1.5" />
        {/* x0 marked */}
        <circle cx={p(0) + PW / 2} cy={120} r={7} fill="#27ae60" stroke="#fff" strokeWidth="2" />
        <text x={p(0) + PW / 2} y={140} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">x₀</text>
        {/* Converging waves */}
        {[30, 50, 70].map((r, i) => (
          <circle key={i} cx={p(0) + PW / 2} cy={120} r={r} fill="none" stroke="#27ae60"
            strokeWidth="1" strokeOpacity={0.3 + i * 0.2} strokeDasharray="4,3" />
        ))}
        <text x={p(0) + PW / 2} y={200} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">Series converges</text>
        <text x={p(0) + PW / 2} y={215} textAnchor="middle" fontSize="11" fill="#555">in |x − x₀| &lt; R</text>

        <rect x={p(0) + 10} y={240} width={PW - 20} height={90} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1" />
        <text x={p(0) + 16} y={258} fontSize="11" fill="#1F3864" fontWeight="700">Condition:</text>
        <text x={p(0) + 16} y={274} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">P(x), Q(x) analytic at x₀</text>
        <text x={p(0) + 16} y={292} fontSize="11" fill="#1F3864" fontWeight="700">Method:</text>
        <text x={p(0) + 16} y={308} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">y = Σ aₙ(x−x₀)ⁿ</text>
        <text x={p(0) + 16} y={324} fontSize="10" fill="#555">Two linearly independent solutions</text>

        {/* Panel 2: Regular singular point */}
        <rect x={p(1)} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={p(1) + PW / 2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Regular Singular Point</text>

        <line x1={p(1) + 20} y1={120} x2={p(1) + PW - 20} y2={120} stroke="#1F3864" strokeWidth="1.5" />
        <circle cx={p(1) + PW / 2} cy={120} r={7} fill="#e07b00" stroke="#fff" strokeWidth="2" />
        <text x={p(1) + PW / 2} y={140} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">x₀</text>
        {/* Partial circles - converge on one side */}
        {[28, 50, 68].map((r, i) => (
          <path key={i}
            d={`M ${p(1) + PW / 2 - r},120 A ${r} ${r} 0 0 1 ${p(1) + PW / 2 + r},120`}
            fill="none" stroke="#e07b00" strokeWidth="1"
            strokeOpacity={0.3 + i * 0.25} strokeDasharray="4,3" />
        ))}
        <text x={p(1) + PW / 2} y={200} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">Frobenius series</text>
        <text x={p(1) + PW / 2} y={215} textAnchor="middle" fontSize="11" fill="#555">y = |x−x₀|ʳ Σ aₙ(x−x₀)ⁿ</text>

        <rect x={p(1) + 10} y={240} width={PW - 20} height={90} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1" />
        <text x={p(1) + 16} y={258} fontSize="11" fill="#1F3864" fontWeight="700">Condition:</text>
        <text x={p(1) + 16} y={274} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">(x−x₀)P, (x−x₀)²Q analytic</text>
        <text x={p(1) + 16} y={292} fontSize="11" fill="#1F3864" fontWeight="700">Method:</text>
        <text x={p(1) + 16} y={308} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Method of Frobenius</text>
        <text x={p(1) + 16} y={324} fontSize="10" fill="#555">Indicial equation gives exponent r</text>

        {/* Panel 3: Irregular singular point */}
        <rect x={p(2)} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={p(2) + PW / 2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Irregular Singular Point</text>

        <line x1={p(2) + 20} y1={120} x2={p(2) + PW - 20} y2={120} stroke="#1F3864" strokeWidth="1.5" />
        <circle cx={p(2) + PW / 2} cy={120} r={7} fill="#c0392b" stroke="#fff" strokeWidth="2" />
        <text x={p(2) + PW / 2} y={140} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">x₀</text>
        {/* Diverging spiky lines */}
        {[0, 40, 80, 120, 160, 200, 240, 280, 320].map((deg, i) => {
          const rad = deg * Math.PI / 180;
          const r1 = 25, r2 = 50 + (i % 3) * 12;
          return (
            <line key={i}
              x1={p(2) + PW / 2 + r1 * Math.cos(rad)} y1={120 + r1 * Math.sin(rad)}
              x2={p(2) + PW / 2 + r2 * Math.cos(rad)} y2={120 + r2 * Math.sin(rad)}
              stroke="#c0392b" strokeWidth="1" strokeOpacity="0.4" />
          );
        })}
        <text x={p(2) + PW / 2} y={200} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Power series diverges</text>
        <text x={p(2) + PW / 2} y={215} textAnchor="middle" fontSize="11" fill="#555">No Frobenius solution guaranteed</text>

        <rect x={p(2) + 10} y={240} width={PW - 20} height={90} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1" />
        <text x={p(2) + 16} y={258} fontSize="11" fill="#1F3864" fontWeight="700">Condition:</text>
        <text x={p(2) + 16} y={274} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Does not satisfy regular condition</text>
        <text x={p(2) + 16} y={292} fontSize="11" fill="#1F3864" fontWeight="700">Approach:</text>
        <text x={p(2) + 16} y={308} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Asymptotic / numerical methods</text>
        <text x={p(2) + 16} y={324} fontSize="10" fill="#555">Beyond standard series treatment</text>

      </svg>
      <div style={S.dCaption}>The type of singular point controls which series method applies. For ordinary points the standard power series works. For regular singular points, the Method of Frobenius extends the approach by allowing a leading factor |x−x₀|ʳ. Irregular singular points require more advanced techniques.</div>
    </div>
  );
}

// ── Diagram 3: Method of Frobenius & Indicial Equation ───────────
function DiagramFrobenius() {
  const W = 900, H = 370;

  // Left: step-by-step flowchart
  const steps = [
    { label: '1. Write ODE in standard form', note: "y'' + P(x)y' + Q(x)y = 0", color: '#2E75B6', bg: '#EBF3FB', bc: '#2E75B6' },
    { label: '2. Check x₀ is regular singular', note: '(x−x₀)P, (x−x₀)²Q analytic', color: '#27ae60', bg: '#f0fff4', bc: '#27ae60' },
    { label: '3. Assume Frobenius series', note: 'y = Σ aₙ(x−x₀)^(n+r)', color: '#e07b00', bg: '#fff7ee', bc: '#e07b00' },
    { label: '4. Substitute into ODE', note: "Collect by powers of (x−x₀)", color: '#9b59b6', bg: '#f5d0ff', bc: '#9b59b6' },
    { label: '5. Indicial equation (n=0 term)', note: 'r(r−1) + p₀r + q₀ = 0', color: '#c0392b', bg: '#fff0f0', bc: '#c0392b' },
    { label: '6. Solve for r₁, r₂', note: 'Two roots → two solutions', color: '#1F3864', bg: '#e8eaf6', bc: '#1F3864' },
  ];

  const bx = 14, bw = 340, bh = 46, gap = 10;

  // Right: indicial equation roots cases
  const cases = [
    { label: 'r₁ − r₂ ∉ ℤ', title: 'Non-integer difference', sol: 'y₁ = |x|^r₁ Σ aₙxⁿ\ny₂ = |x|^r₂ Σ bₙxⁿ', color: '#2E75B6', bg: '#EBF3FB', bc: '#2E75B6' },
    { label: 'r₁ = r₂', title: 'Repeated root', sol: 'y₁ = |x|^r Σ aₙxⁿ\ny₂ = y₁ ln|x| + |x|^r Σ bₙxⁿ', color: '#e07b00', bg: '#fff7ee', bc: '#e07b00' },
    { label: 'r₁ − r₂ = N ∈ ℤ⁺', title: 'Integer difference', sol: 'y₁ = |x|^r₁ Σ aₙxⁿ\ny₂ = c·y₁ ln|x| + |x|^r₂ Σ bₙxⁿ', color: '#9b59b6', bg: '#f5d0ff', bc: '#9b59b6' },
  ];

  const rx = bx + bw + 20, rw = W - rx - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Method of Frobenius — Steps and Indicial Root Cases</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left panel */}
        <rect x={bx} y={10} width={bw} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={bx + bw / 2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Frobenius Method Steps</text>

        {steps.map(({ label, note, bg, bc }, i) => {
          const y = 40 + i * (bh + gap);
          return (
            <g key={i}>
              <rect x={bx + 10} y={y} width={bw - 20} height={bh} rx={4} fill={bg} stroke={bc} strokeWidth="1.2" />
              <text x={bx + 20} y={y + 16} fontSize="12" fill="#1F3864" fontWeight="700">{label}</text>
              <text x={bx + 20} y={y + 32} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{note}</text>
              {i < steps.length - 1 && (
                <polygon
                  points={`${bx + bw / 2},${y + bh + 1} ${bx + bw / 2 - 6},${y + bh + gap - 1} ${bx + bw / 2 + 6},${y + bh + gap - 1}`}
                  fill="#bbb" />
              )}
            </g>
          );
        })}

        {/* Right panel */}
        <rect x={rx} y={10} width={rw} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={rx + rw / 2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Indicial Root Cases</text>

        {/* Indicial equation box */}
        <rect x={rx + 10} y={38} width={rw - 20} height={52} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5" />
        <text x={rx + rw / 2} y={56} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Indicial Equation</text>
        <text x={rx + rw / 2} y={73} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace">r² + (p₀−1)r + q₀ = 0</text>
        <text x={rx + rw / 2} y={86} textAnchor="middle" fontSize="10" fill="#555">p₀ = lim(x→x₀) (x−x₀)P(x),  q₀ = lim(x→x₀) (x−x₀)²Q(x)</text>

        {cases.map(({ label, title, sol, bg, bc }, i) => {
          const y = 100 + i * 88;
          return (
            <g key={i}>
              <rect x={rx + 10} y={y} width={rw - 20} height={80} rx={4} fill={bg} stroke={bc} strokeWidth="1.2" />
              <text x={rx + 22} y={y + 17} fontSize="12" fill={bc} fontFamily="'Courier New',monospace" fontWeight="700">{label}</text>
              <text x={rx + 22 + 130} y={y + 17} fontSize="11" fill="#1F3864" fontWeight="700">— {title}</text>
              {sol.split('\n').map((line, j) => (
                <text key={j} x={rx + 22} y={y + 35 + j * 18} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{line}</text>
              ))}
            </g>
          );
        })}

        {/* Note box */}
        <rect x={rx + 10} y={366} width={rw - 20} height={0} rx={3} fill="#f8fbff" stroke="#ccd9ea" strokeWidth="1" />

      </svg>
      <div style={S.dCaption}>The Method of Frobenius handles regular singular points by assuming y = Σ aₙ(x−x₀)^(n+r). Substituting and matching the lowest power gives the indicial equation. The two roots r₁, r₂ dictate whether the second solution is a plain Frobenius series or requires a logarithmic term.</div>
    </div>
  );
}

// ── Diagram 4: Radius of Convergence ────────────────────────────
function DiagramRadiusConvergence() {
  const W = 900, H = 340;

  // Left: number-line picture of convergence interval
  const cx = 230, cy = 120, R = 150;

  // Right: ratio test and common series
  const rx = 480, rw = W - rx - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Radius of Convergence — Interval of Convergence and Ratio Test</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left panel */}
        <rect x={14} y={10} width={450} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={239} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Interval of Convergence on the Number Line</text>

        {/* Full number line */}
        <line x1={40} y1={cy} x2={430} y2={cy} stroke="#1F3864" strokeWidth="1.5" />
        <polygon points={`430,${cy} 422,${cy - 4} 422,${cy + 4}`} fill="#1F3864" />

        {/* Convergence arc */}
        <path d={`M ${cx - R},${cy} A ${R} ${R} 0 0 1 ${cx + R},${cy}`}
          fill="#d0e8ff" fillOpacity="0.5" stroke="#2E75B6" strokeWidth="2" />

        {/* Center and endpoints */}
        <circle cx={cx} cy={cy} r={6} fill="#2E75B6" stroke="#fff" strokeWidth="2" />
        <text x={cx} y={cy - 12} textAnchor="middle" fontSize="13" fill="#2E75B6" fontWeight="700">x₀</text>

        <circle cx={cx - R} cy={cy} r={6} fill="none" stroke="#c0392b" strokeWidth="2" />
        <text x={cx - R} y={cy + 20} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">x₀ − R</text>

        <circle cx={cx + R} cy={cy} r={6} fill="none" stroke="#c0392b" strokeWidth="2" />
        <text x={cx + R} y={cy + 20} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">x₀ + R</text>

        {/* R arrow */}
        <line x1={cx} y1={cy - 32} x2={cx + R} y2={cy - 32} stroke="#27ae60" strokeWidth="1.5" markerEnd="url(#arr)" />
        <line x1={cx} y1={cy - 32} x2={cx - R} y2={cy - 32} stroke="#27ae60" strokeWidth="1.5" />
        <text x={cx + R / 2} y={cy - 38} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">R</text>
        <text x={cx - R / 2} y={cy - 38} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">R</text>

        {/* Converge / diverge labels */}
        <text x={cx} y={cy + 60} textAnchor="middle" fontSize="13" fill="#2E75B6" fontWeight="700">Converges: |x − x₀| &lt; R</text>
        <text x={cx - R - 30} y={cy + 60} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Diverges</text>
        <text x={cx + R + 30} y={cy + 60} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Diverges</text>

        {/* Three formulas for R */}
        {[
          { y: 155, bg: '#EBF3FB', bc: '#2E75B6', title: 'Ratio Test', formula: 'R = lim |aₙ / aₙ₊₁|  as n → ∞' },
          { y: 210, bg: '#f0fff4', bc: '#27ae60', title: 'Root Test', formula: 'R = 1 / limsup |aₙ|^(1/n)' },
          { y: 265, bg: '#fff7ee', bc: '#e07b00', title: 'Distance to singularity', formula: 'R = dist(x₀, nearest singular pt)' },
        ].map(({ y, bg, bc, title, formula }) => (
          <g key={title}>
            <rect x={24} y={y} width={430} height={46} rx={3} fill={bg} stroke={bc} strokeWidth="1" />
            <text x={32} y={y + 16} fontSize="12" fill="#1F3864" fontWeight="700">{title}:</text>
            <text x={32} y={y + 33} fontSize="12" fill={bc} fontFamily="'Courier New',monospace">{formula}</text>
          </g>
        ))}

        {/* Right panel */}
        <rect x={rx} y={10} width={rw} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={rx + rw / 2} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Common Series & Radii</text>

        {[
          { fn: 'eˣ', series: '1 + x + x²/2! + x³/3! + …', R: 'R = ∞', color: '#2E75B6', bg: '#EBF3FB' },
          { fn: 'sin x', series: 'x − x³/3! + x⁵/5! − …', R: 'R = ∞', color: '#27ae60', bg: '#f0fff4' },
          { fn: 'cos x', series: '1 − x²/2! + x⁴/4! − …', R: 'R = ∞', color: '#e07b00', bg: '#fff7ee' },
          { fn: '1/(1−x)', series: '1 + x + x² + x³ + …', R: 'R = 1', color: '#c0392b', bg: '#fff0f0' },
          { fn: 'ln(1+x)', series: 'x − x²/2 + x³/3 − …', R: 'R = 1', color: '#9b59b6', bg: '#f5d0ff' },
        ].map(({ fn, series, R, color, bg }, i) => {
          const y = 40 + i * 56;
          return (
            <g key={fn}>
              <rect x={rx + 8} y={y} width={rw - 16} height={48} rx={3} fill={bg} stroke={color} strokeWidth="1" />
              <text x={rx + 16} y={y + 16} fontSize="13" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{fn}</text>
              <text x={rx + 16} y={y + 31} fontSize="10" fill="#444" fontFamily="'Courier New',monospace">{series}</text>
              <rect x={rx + rw - 68} y={y + 6} width={58} height={20} rx={3} fill={color} fillOpacity="0.15" />
              <text x={rx + rw - 39} y={y + 20} textAnchor="middle" fontSize="11" fill={color} fontWeight="700">{R}</text>
            </g>
          );
        })}

      </svg>
      <div style={S.dCaption}>The radius of convergence R is the half-width of the interval around x₀ where the series converges. It can be computed by the ratio test, the root test, or by finding the nearest singular point of the ODE in the complex plane. Endpoints require separate testing.</div>
    </div>
  );
}

// ── Diagram 5: Legendre & Bessel Equations ───────────────────────
function DiagramSpecialEquations() {
  const W = 900, H = 380;
  const PW = (W - 42) / 2;
  const p = i => 14 + i * (PW + 14);

  // Legendre polynomials P0..P4 at sample x values
  function legendre(n, x) {
    if (n === 0) return 1;
    if (n === 1) return x;
    if (n === 2) return (3 * x * x - 1) / 2;
    if (n === 3) return (5 * x * x * x - 3 * x) / 2;
    if (n === 4) return (35 * x ** 4 - 30 * x * x + 3) / 8;
    return 0;
  }

  // Bessel J0, J1 (partial sum approximation)
  function besselJ(nu, x, terms = 15) {
    let s = 0;
    for (let m = 0; m < terms; m++) {
      let fact_m = 1;
      for (let k = 1; k <= m; k++) fact_m *= k;
      let fact_mnu = 1;
      for (let k = 1; k <= m + nu; k++) fact_mnu *= k;
      s += ((-1) ** m / (fact_m * fact_mnu)) * (x / 2) ** (2 * m + nu);
    }
    return s;
  }

  // SVG coords for Legendre panel
  const lox = p(0) + 60, loy = 200, lsc = 90;
  const ltx = x => lox + x * lsc;
  const lty = y => loy - y * lsc;

  const lxVals = [];
  for (let i = -100; i <= 100; i++) lxVals.push(i * 0.01);

  const legendreCurves = [
    { n: 0, color: '#2E75B6' },
    { n: 1, color: '#27ae60' },
    { n: 2, color: '#e07b00' },
    { n: 3, color: '#c0392b' },
    { n: 4, color: '#9b59b6' },
  ];

  function lPts(n) {
    return lxVals
      .map(x => {
        const y = legendre(n, x);
        if (y > 1.4 || y < -1.4) return null;
        return `${ltx(x)},${lty(y)}`;
      })
      .filter(Boolean)
      .join(' ');
  }

  // Bessel panel coords
  const box = p(1) + 60, boy = 195, bsc = 28;
  const btx = x => box + x * bsc;
  const bty = y => boy - y * bsc;

  const bxVals = [];
  for (let i = 1; i <= 200; i++) bxVals.push(i * 0.05);

  function bPts(nu) {
    return bxVals
      .map(x => {
        const y = besselJ(nu, x);
        if (y > 1.3 || y < -0.55) return null;
        return `${btx(x)},${bty(y)}`;
      })
      .filter(Boolean)
      .join(' ');
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Classic ODE Series Solutions — Legendre Polynomials and Bessel Functions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* ── Legendre panel ── */}
        <rect x={p(0)} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={p(0) + PW / 2} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Legendre Polynomials Pₙ(x)</text>

        {/* Grid */}
        {[-1, -0.5, 0, 0.5, 1].map(v => (
          <g key={v}>
            <line x1={ltx(-1)} y1={lty(v)} x2={ltx(1)} y2={lty(v)} stroke="#eee" strokeWidth="1" />
            <text x={lox - 8} y={lty(v) + 4} fontSize="10" fill="#bbb" textAnchor="end">{v}</text>
          </g>
        ))}
        {[-1, -0.5, 0, 0.5, 1].map(v => (
          <g key={v}>
            <line x1={ltx(v)} y1={lty(-1.3)} x2={ltx(v)} y2={lty(1.3)} stroke="#eee" strokeWidth="1" />
            <text x={ltx(v)} y={lty(-1.3) + 14} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={ltx(-1.05)} y1={loy} x2={ltx(1.05)} y2={loy} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={lox} y1={lty(1.3)} x2={lox} y2={lty(-1.3)} stroke="#1F3864" strokeWidth="1.5" />

        {legendreCurves.map(({ n, color }) => (
          <polyline key={n} points={lPts(n)} fill="none" stroke={color} strokeWidth="2" />
        ))}

        {/* Legend */}
        {legendreCurves.map(({ n, color }, i) => (
          <g key={n}>
            <line x1={p(0) + 18} y1={282 + i * 16} x2={p(0) + 42} y2={282 + i * 16} stroke={color} strokeWidth="2" />
            <text x={p(0) + 48} y={286 + i * 16} fontSize="11" fill={color} fontWeight="700">P{n}(x)</text>
          </g>
        ))}

        {/* Legendre ODE box */}
        <rect x={p(0) + 10} y={H - 58} width={PW - 20} height={44} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1" />
        <text x={p(0) + 18} y={H - 43} fontSize="11" fill="#1F3864" fontWeight="700">Legendre's Equation:</text>
        <text x={p(0) + 18} y={H - 27} fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">(1−x²)y'' − 2xy' + n(n+1)y = 0</text>

        {/* ── Bessel panel ── */}
        <rect x={p(1)} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={p(1) + PW / 2} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Bessel Functions Jν(x)</text>

        {/* Grid */}
        {[-0.5, 0, 0.5, 1].map(v => (
          <g key={v}>
            <line x1={btx(0)} y1={bty(v)} x2={btx(10)} y2={bty(v)} stroke="#eee" strokeWidth="1" />
            <text x={box - 8} y={bty(v) + 4} fontSize="10" fill="#bbb" textAnchor="end">{v}</text>
          </g>
        ))}
        {[0, 2, 4, 6, 8, 10].map(v => (
          <g key={v}>
            <line x1={btx(v)} y1={bty(-0.55)} x2={btx(v)} y2={bty(1.3)} stroke="#eee" strokeWidth="1" />
            <text x={btx(v)} y={bty(-0.55) + 14} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={btx(0)} y1={boy} x2={btx(10.2)} y2={boy} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={box} y1={bty(1.3)} x2={box} y2={bty(-0.55)} stroke="#1F3864" strokeWidth="1.5" />

        {[
          { nu: 0, color: '#2E75B6' },
          { nu: 1, color: '#27ae60' },
          { nu: 2, color: '#e07b00' },
          { nu: 3, color: '#c0392b' },
        ].map(({ nu, color }) => (
          <polyline key={nu} points={bPts(nu)} fill="none" stroke={color} strokeWidth="2" />
        ))}

        {/* Bessel legend */}
        {[
          { nu: 0, color: '#2E75B6' },
          { nu: 1, color: '#27ae60' },
          { nu: 2, color: '#e07b00' },
          { nu: 3, color: '#c0392b' },
        ].map(({ nu, color }, i) => (
          <g key={nu}>
            <line x1={p(1) + 18} y1={282 + i * 16} x2={p(1) + 42} y2={282 + i * 16} stroke={color} strokeWidth="2" />
            <text x={p(1) + 48} y={286 + i * 16} fontSize="11" fill={color} fontWeight="700">J{nu}(x)</text>
          </g>
        ))}

        {/* Bessel ODE box */}
        <rect x={p(1) + 10} y={H - 58} width={PW - 20} height={44} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1" />
        <text x={p(1) + 18} y={H - 43} fontSize="11" fill="#1F3864" fontWeight="700">Bessel's Equation of order ν:</text>
        <text x={p(1) + 18} y={H - 27} fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">x²y'' + xy' + (x²−ν²)y = 0</text>

      </svg>
      <div style={S.dCaption}>Legendre polynomials Pₙ(x) are the polynomial solutions of Legendre's equation on [−1,1]; they arise in physics problems with spherical symmetry. Bessel functions Jν(x) solve Bessel's equation and are generated by a Frobenius series at the regular singular point x₀ = 0; they appear in cylindrical and circular wave problems.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function PowerSeriesODEs() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Power Series Solutions of Ordinary Differential Equations</h1>
      <p style={S.p}>When a differential equation has variable coefficients that prevent closed-form solutions, the power series method provides a systematic way to represent the solution as an infinite polynomial. The key idea is to assume y = Σ aₙ(x − x₀)ⁿ, substitute into the ODE, and match coefficients power by power to determine the aₙ. This technique, extended by the Method of Frobenius for singular points, produces two linearly independent solutions essential for the general solution.</p>

      <InfoBox title="Core Idea: Assume y is an infinite polynomial, substitute, and match coefficients" items={[
        "Power series: y = Σ aₙ(x−x₀)ⁿ — works near an ordinary point x₀",
        "Ordinary point: P(x) and Q(x) in y''+P y'+Q y=0 are analytic (Taylor-expandable) at x₀",
        "Regular singular point: (x−x₀)P and (x−x₀)²Q are analytic — use Frobenius method",
        "Irregular singular point: Frobenius fails; asymptotic or numerical methods required",
        "Radius of convergence R: distance from x₀ to the nearest singularity in the complex plane",
        "Two linearly independent solutions always exist; their form depends on the indicial roots",
      ]} />

      <h2 style={S.h2}>The Power Series and Its Anatomy</h2>
      <DiagramPowerSeries />
      <Table2 rows={[
        ["Power series (general)", "y = Σ aₙ(x−x₀)ⁿ,  n = 0 to ∞\nExpansion about the ordinary point x₀"],
        ["Term-by-term differentiation", "y' = Σ n·aₙ(x−x₀)^(n−1)\ny'' = Σ n(n−1)·aₙ(x−x₀)^(n−2)"],
        ["Re-indexing", "Shift index m = n−k to align powers\nEssential for combining sums over the same power"],
        ["Recurrence relation", "After substitution, equate each power to zero\nExpresses aₙ₊₂ (or aₙ₊ₖ) in terms of earlier coefficients"],
        ["Two free constants", "a₀ and a₁ remain arbitrary → general solution\nEach generates one linearly independent solution"],
        ["eˣ example", "y'=y at x₀=0: aₙ₊₁ = aₙ/(n+1) → aₙ = 1/n!\nGives y = Σ xⁿ/n! = eˣ  (R = ∞)"],
      ]} />

      <h2 style={S.h2}>Classification of Points</h2>
      <DiagramSingularPoints />
      <Table2 rows={[
        ["Ordinary point x₀", "P(x) = p(x)/q(x) analytic at x₀  (q(x₀) ≠ 0)\nStandard power series converges in |x−x₀| < R"],
        ["Singular point x₀", "P(x) or Q(x) not analytic at x₀\nStandard series fails; classify further"],
        ["Regular singular point", "(x−x₀)P(x) and (x−x₀)²Q(x) both analytic at x₀\nFrobenius method applies"],
        ["Irregular singular point", "Conditions for regular singular point not met\nNo guaranteed convergent series; advanced methods needed"],
        ["Nearest singularity rule", "R ≥ distance from x₀ to nearest singular point in ℂ\nExplains why 1/(1−x) expanded at 0 has R=1"],
        ["Analytic function", "Equals its Taylor series in a neighborhood\nAll polynomials, eˣ, sin, cos are entire (R=∞)"],
      ]} />

      <h2 style={S.h2}>Method of Frobenius</h2>
      <DiagramFrobenius />
      <Table2 rows={[
        ["Frobenius series", "y = |x−x₀|ʳ · Σ aₙ(x−x₀)ⁿ  (a₀ ≠ 0)\nr is determined by the indicial equation"],
        ["Indicial equation", "r(r−1) + p₀r + q₀ = 0\np₀ = lim(x→x₀)(x−x₀)P,  q₀ = lim(x→x₀)(x−x₀)²Q"],
        ["Roots not differing by integer", "r₁ − r₂ ∉ ℤ: two independent Frobenius series\nOne for each root r₁, r₂"],
        ["Repeated roots r₁ = r₂", "y₂ = y₁ ln|x−x₀| + |x−x₀|^r₁ · Σ bₙ(x−x₀)ⁿ\nLogarithmic term always present"],
        ["Integer difference r₁−r₂ = N", "Second solution may (or may not) include ln term\ny₂ = c·y₁ ln|x−x₀| + |x−x₀|^r₂ · Σ bₙ(x−x₀)ⁿ"],
        ["Wronskian test", "W(y₁,y₂) ≠ 0 confirms linear independence\nAbel's theorem: W = C·exp(−∫P dx)"],
      ]} />

      <h2 style={S.h2}>Radius and Interval of Convergence</h2>
      <DiagramRadiusConvergence />
      <Table2 rows={[
        ["Ratio test for R", "R = lim_{n→∞} |aₙ/aₙ₊₁|\nMost convenient when recurrence is known"],
        ["Root test for R", "1/R = limsup_{n→∞} |aₙ|^(1/n)\nHadamard's formula — always valid"],
        ["Nearest singularity", "R = min distance from x₀ to singular points in ℂ\nCovers complex singularities (e.g. 1/(1+x²) at x₀=0 has R=1)"],
        ["Endpoint behaviour", "Convergence at x₀ ± R must be checked separately\nSeries may converge, converge conditionally, or diverge at endpoints"],
        ["Differentiation / integration", "R unchanged by term-by-term differentiation or integration\nAllows y', y'' to be computed within same interval"],
        ["Taylor series at ordinary point", "R ≥ dist(x₀, nearest singularity of P,Q)\nSolution inherits analyticity from the ODE coefficients"],
      ]} />

      <h2 style={S.h2}>Classic Equations: Legendre and Bessel</h2>
      <DiagramSpecialEquations />
      <Table2 rows={[
        ["Legendre's equation", "(1−x²)y'' − 2xy' + n(n+1)y = 0\nOrdinary points everywhere except x = ±1"],
        ["Legendre polynomials Pₙ(x)", "Polynomial solution when n is a non-negative integer\nOrthogonal on [−1,1]: ∫₋₁¹ Pₘ Pₙ dx = 2/(2n+1)·δₘₙ"],
        ["Rodrigues' formula", "Pₙ(x) = (1/2ⁿ n!) · dⁿ/dxⁿ[(x²−1)ⁿ]\nAlternative to series derivation"],
        ["Bessel's equation (order ν)", "x²y'' + xy' + (x²−ν²)y = 0\nRegular singular point at x₀ = 0; Frobenius gives Jν"],
        ["Bessel function Jν(x)", "Jν(x) = Σ (−1)ᵐ / (m! Γ(m+ν+1)) · (x/2)^(2m+ν)\nOscillatory, infinitely many zeros"],
        ["Second Bessel solution Yν", "When ν is an integer, second solution involves ln\nYν(x) singular at x=0; excluded when solution must be bounded"],
      ]} />

      <h2 style={S.h2}>Worked Example: Airy Equation</h2>
      <Table2 rows={[
        ["Airy equation", "y'' − xy = 0\nOrdinary point at x₀ = 0; all coefficients analytic → R = ∞"],
        ["Assume series", "y = Σ aₙxⁿ → y'' = Σ n(n−1)aₙx^(n−2)\nxy = Σ aₙx^(n+1)"],
        ["Re-index and equate", "Σ n(n−1)aₙx^(n−2) = Σ aₙx^(n+1)\nShift: Σ (n+2)(n+1)aₙ₊₂x^n = Σ aₙ₋₁xⁿ"],
        ["Recurrence relation", "aₙ₊₃ = aₙ / ((n+2)(n+3))\nEvery third coefficient linked; others zero unless seeded"],
        ["Solution y₁ (a₀=1, a₁=0)", "y₁ = 1 + x³/(2·3) + x⁶/(2·3·5·6) + …\nAiry function Ai(x) (decays for x>0)"],
        ["Solution y₂ (a₀=0, a₁=1)", "y₂ = x + x⁴/(3·4) + x⁷/(3·4·6·7) + …\nAiry function Bi(x) (grows for x>0)"],
      ]} />

      <InfoBox title="Key Takeaway — Which Method to Use and When" items={[
        "Ordinary point at x₀, want solution near x₀? → Standard power series y = Σ aₙ(x−x₀)ⁿ",
        "Regular singular point? → Frobenius: y = |x−x₀|ʳ Σ aₙ(x−x₀)ⁿ; find r from indicial equation",
        "Roots differ by non-integer? → Two straightforward Frobenius series, one per root",
        "Repeated roots? → First series normal; second series includes y₁ ln|x−x₀| term",
        "Integer difference of roots? → Use reduction of order or variation of parameters for y₂",
        "Radius of convergence? → Ratio test on aₙ, or distance to nearest singularity of P(x), Q(x) in ℂ",
        "Recognise a Legendre form? → Polynomial solution Pₙ(x) exists iff parameter n is a non-negative integer",
        "Recognise a Bessel form? → Jν and Yν; Yν singular at origin, exclude if bounded solution needed",
      ]} />
    </div>
  );
}